import React, { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from '@ygtechnologies/rubus-ui';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { useAsyncState, useAsyncStateWithIsLoading, useLocalStorage } from '@/utils/hooks';
import {
    EmailContentResolution,
    createContentOverride,
    deleteContentOverride,
    getEmailContent,
    resolveEmailContent,
    updateContentOverride,
} from '@/services/emailTemplates';
import ScopeBar from './ScopeBar';
import TemplateEditorPanel, { TemplateEditorTarget } from './TemplateEditorPanel';
import OverridesSummary, { ShowOverridesButton } from './OverridesSummary';
import { ScopeState, editScopeOf, languageLabel, useScopeInUrl, useScopeNamesFromResolution } from './shared';
import theme from './EmailTemplates.module.scss';

/**
 * The row the chosen scope edits, seeded from the tier above when it does not exist there yet. Pure, so
 * the mapping from scope to editor state can be read in one place.
 */
function buildTarget(scope: ScopeState, resolution: EmailContentResolution): TemplateEditorTarget {
    const editScope = editScopeOf(scope);
    const language = languageLabel(scope.languageCode);
    const common = {
        kind: 'content' as const,
        hasSubject: true,
        languageCode: scope.languageCode,
        projectId: scope.projectId,
        profileId: scope.profileId,
    };

    if (editScope === 'global') {
        return {
            ...common,
            key: `global-${resolution.global?.id}`,
            readOnly: true,
            isNew: false,
            subject: resolution.global?.subject ?? '',
            template: resolution.global?.template ?? '',
            sourceNote: `Global template — ${languageLabel(resolution.global?.languageCode)}`,
        };
    }

    if (editScope === 'profile') {
        const existing = resolution.profileOverride;
        return {
            ...common,
            key: `profile-${scope.profileId}-${scope.languageCode ?? 'any'}-${existing?.id ?? 'new'}`,
            readOnly: false,
            isNew: !existing,
            subject: existing?.subject ?? resolution.global?.subject ?? '',
            template: existing?.template ?? resolution.global?.template ?? '',
            sourceNote: existing
                ? `Seller profile override for ${resolution.profileName} — ${language}`
                : `New seller profile override for ${resolution.profileName} — ${language}, seeded from the global template`,
        };
    }

    const existing = resolution.projectOverride;
    const seededFrom =
        resolution.source === 'project-override'
            ? 'this project’s override for another language'
            : resolution.source === 'profile-override'
              ? 'the seller profile override'
              : 'the global template';
    return {
        ...common,
        key: `project-${scope.projectId}-${scope.languageCode ?? 'any'}-${existing?.id ?? 'new'}`,
        readOnly: false,
        isNew: !existing,
        subject: existing?.subject ?? resolution.subject ?? '',
        template: existing?.template ?? resolution.template ?? '',
        sourceNote: existing
            ? `Project override for ${resolution.projectName} — ${language}`
            : `New project override for ${resolution.projectName} — ${language}, seeded from ${seededFrom}`,
    };
}

/** The override row the current edit scope maps to, or null when it does not exist yet. */
function overrideAtScope(scope: ScopeState, resolution: EmailContentResolution) {
    const editScope = editScopeOf(scope);
    if (editScope === 'project') {
        return resolution.projectOverride;
    }
    if (editScope === 'profile') {
        return resolution.profileOverride;
    }
    return null;
}

/**
 * One content template. The bar picks the scope — seller profile, project, language — and everything
 * below shows and edits the template for that scope, so the preview can never imply a scope the editor
 * does not have.
 */
const EmailContentDetails: React.FC = () => {
    const { name: nameParam } = useParams<{ name: string }>();
    // React Router v5 does not decode route params, and the service layer encodes when building URLs.
    const name = decodeURIComponent(nameParam);
    const location = useLocation();
    const [scope, setScope] = useScopeInUrl();
    // Remembered across templates and visits, like the preview toggle.
    const [showOverrides, setShowOverrides] = useLocalStorage('emailTemplateShowOverrides', true);

    // Full override list: powers the summary and the "already overridden" markers in the pickers.
    const [detail, , reloadDetail] = useAsyncState(() => getEmailContent(name), [name], null);

    const [resolution, , reloadResolution, isResolving] = useAsyncStateWithIsLoading(
        () =>
            resolveEmailContent(name, {
                projectId: scope.projectId,
                profileId: scope.profileId,
                languageCode: scope.languageCode,
            }),
        [name, scope.projectId, scope.profileId, scope.languageCode],
        null
    );

    // Deep links carry only ids; the resolution response supplies the picker labels for them.
    useScopeNamesFromResolution(scope, setScope, resolution);

    const overriddenProjectIds = useMemo(
        () => new Set((detail?.overrides ?? []).map((x) => x.projectId).filter((x): x is number => !!x)),
        [detail]
    );
    const overriddenProfileIds = useMemo(
        () => new Set((detail?.overrides ?? []).map((x) => x.profileId).filter((x): x is number => !!x)),
        [detail]
    );
    // Globals plus overrides, so an override's language is always selectable even when no global row
    // exists in that language.
    const languages = useMemo(
        () =>
            [...(detail?.globals ?? []), ...(detail?.overrides ?? [])]
                .map((x) => x.languageCode)
                .filter((x): x is string => !!x)
                .filter((x, index, all) => all.indexOf(x) === index),
        [detail]
    );

    if (!detail || !resolution) {
        return <LoadingIndicator />;
    }

    const reload = async () => {
        await Promise.all([reloadDetail(), reloadResolution()]);
    };

    const existing = overrideAtScope(scope, resolution);

    const handleSave = async (values: { subject: string; template: string }) => {
        if (existing) {
            await updateContentOverride(existing.id, values);
        } else {
            await createContentOverride({
                name: detail.name,
                languageCode: scope.languageCode,
                // Exactly one scope: the most specific selection in the bar.
                projectId: editScopeOf(scope) === 'project' ? scope.projectId : null,
                profileId: editScopeOf(scope) === 'profile' ? scope.profileId : null,
                ...values,
            });
        }

        await reload();
    };

    const handleDelete = existing
        ? async () => {
              await deleteContentOverride(existing.id);
              await reload();
          }
        : undefined;

    return (
        <div>
            <div className={theme.detailsHeader}>
                <div>
                    <Button
                        as={Link}
                        // Keeps the scope, so the list comes back focused on the same project/profile.
                        to={`/email-templates/contents${location.search}`}
                        size="xs"
                        variant="tertiary"
                        icon={faArrowLeft}>
                        All templates
                    </Button>
                    <h2>{detail.name}</h2>
                </div>
            </div>

            <ScopeBar
                scope={scope}
                onChange={setScope}
                languages={languages}
                overriddenProjectIds={overriddenProjectIds}
                overriddenProfileIds={overriddenProfileIds}
            />

            <div className={theme.detailsLayout}>
                {showOverrides && (
                    <OverridesSummary
                        overrides={detail.overrides}
                        activeId={existing?.id ?? null}
                        onHide={() => setShowOverrides(false)}
                        onSelect={(override) =>
                            setScope({
                                projectId: override.projectId,
                                projectName: override.projectId ? override.scopeName : null,
                                profileId: override.profileId,
                                profileName: override.profileId ? override.scopeName : null,
                                languageCode: override.languageCode,
                            })
                        }
                    />
                )}

                <div className={theme.detailsMain}>
                    <TemplateEditorPanel
                        target={buildTarget(scope, resolution)}
                        // The shown resolution may still belong to the previous scope while the new one
                        // loads; acting on it then would hit the wrong override row.
                        actionsDisabled={isResolving}
                        headerActions={
                            !showOverrides && (
                                <ShowOverridesButton
                                    count={detail.overrides.length}
                                    onClick={() => setShowOverrides(true)}
                                />
                            )
                        }
                        onSave={handleSave}
                        onDelete={handleDelete}
                    />
                </div>
            </div>
        </div>
    );
};

export default EmailContentDetails;
